import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "app/store";

interface PatientFiltersState {
  isColumnChecked: boolean;
  rowsDifferentFromColumState: Record<number, boolean>;
}

const initialState = {
  isColumnChecked: false,
  rowsDifferentFromColumState: {}
} as PatientFiltersState;

const patientFiltersSlice = createSlice({
  name: "patient-filters",
  initialState,
  reducers: {
    applyColumnChecked(state, action: PayloadAction<boolean>) {
      const isInterdeterminate = getIsInterdeterminate(
        state.rowsDifferentFromColumState
      );

      if (isInterdeterminate) {
        state.isColumnChecked = true;
      } else {
        state.isColumnChecked = action.payload;
      }
      state.rowsDifferentFromColumState = {};
    },
    applyRowChecked(
      state,
      action: PayloadAction<{
        patientId: number;
        checked: boolean;
        patientsCount: number;
      }>
    ) {
      const { rowsDifferentFromColumState, isColumnChecked } = state;
      const { patientId, checked, patientsCount } = action.payload;

      rowsDifferentFromColumState[patientId] = checked !== isColumnChecked;

      const allRowsAreDifferent =
        Object.entries(rowsDifferentFromColumState).length === patientsCount;

      if (checked && allRowsAreDifferent) {
        state.isColumnChecked = true;
        state.rowsDifferentFromColumState = {};
      } else if (!checked && allRowsAreDifferent) {
        state.isColumnChecked = false;
        state.rowsDifferentFromColumState = {};
      }
    },
    clearCheckboxes(state) {
      state.isColumnChecked = false;
      state.rowsDifferentFromColumState = {};
    }
  }
});

const getIsInterdeterminate = (rows: Record<number, boolean>) => {
  return Boolean(
    Object.entries(rows).filter(([, isDifferent]) => isDifferent).length
  );
};

export const selectPatientCheckboxes = (state: RootState) => {
  const { rowsDifferentFromColumState, isColumnChecked } = state.patientFilters;

  return {
    interdeterminate: getIsInterdeterminate(rowsDifferentFromColumState),
    isColumnChecked: isColumnChecked,
    rowsDifferentFromColumState
  };
};

export const { applyColumnChecked, applyRowChecked, clearCheckboxes } =
  patientFiltersSlice.actions;

export default patientFiltersSlice.reducer;
